/**
 * Collection Filtering, Sorting, and Infinite Scroll
 * Preloads all products on page load and reveals them as user scrolls.
 * Uses native fetch (no axios dependency).
 *
 * Also contains the collection-page widgets that live outside the compiled
 * app.js bundle: custom sort dropdown, short-description toggle, sticky
 * filter bar offsets and the price range slider.
 */
document.addEventListener('DOMContentLoaded', function() {
    const filterForm = document.querySelector('#filters');
    const sortSelects = document.querySelectorAll('.js-sort-by');
    const productGrid = document.querySelector('#products-grid');
    const infiniteScrollTrigger = document.querySelector('#infinite-scroll-trigger');
    const loadingIndicator = document.querySelector('#infinite-scroll-loading');
    const endMessage = document.querySelector('#infinite-scroll-end');

    const fetchHtml = (url) => fetch(url).then(response => response.text());

    // Theme runs without a doctype (quirks mode) where window.scrollTo is
    // unreliable — try every scroll mechanism plus the products anchor.
    const scrollPageToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
        const anchor = document.getElementById('products-top');
        if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    // Exit early if required elements are not found
    if (!filterForm || sortSelects.length === 0 || !productGrid) {
        return;
    }

    // Initialize Shopify query params object if not exists
    if (typeof Shopify === 'undefined') {
        window.Shopify = {};
    }
    Shopify.queryParams = {};

    // Preload state
    let currentPage = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.currentPage) : 1;
    let totalPages = infiniteScrollTrigger ? parseInt(infiniteScrollTrigger.dataset.totalPages) : 1;
    let preloadedProducts = [];
    let isPreloading = false;
    let preloadComplete = false;
    let revealObserver = null;

    /**
     * Get query parameters from the current URL
     */
    const getQueryParams = () => {
        const queryParams = {};
        if (location.search.length) {
            const params = new URLSearchParams(location.search);
            params.forEach((value, key) => {
                if (queryParams[key]) {
                    queryParams[key] = [].concat(queryParams[key], value);
                } else {
                    queryParams[key] = value;
                }
            });
        }
        return queryParams;
    };

    Shopify.queryParams = getQueryParams();

    /**
     * Build URL with current filters, sorting, and page number
     */
    const buildUrlWithParams = (pageNum) => {
        const queryParams = { ...Shopify.queryParams };
        queryParams.page = pageNum;

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            if (Array.isArray(queryParams[key])) {
                queryParams[key].forEach(value => searchParams.append(key, value));
            } else {
                searchParams.append(key, queryParams[key]);
            }
        });

        return `${window.location.pathname}?${searchParams.toString()}`;
    };

    /**
     * Preload all remaining pages in the background
     */
    const preloadAllProducts = async () => {
        if (isPreloading || currentPage >= totalPages) {
            preloadComplete = true;
            if (loadingIndicator) loadingIndicator.classList.add('d-none');
            return;
        }

        isPreloading = true;

        for (let page = currentPage + 1; page <= totalPages; page++) {
            try {
                const url = buildUrlWithParams(page);
                const html = await fetchHtml(url);
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                const newProductsContainer = doc.querySelector('#products-grid');
                if (newProductsContainer) {
                    const newProducts = newProductsContainer.querySelectorAll(':scope > div');
                    newProducts.forEach(product => {
                        preloadedProducts.push(product.cloneNode(true));
                    });
                }
            } catch (error) {
                console.error('Error preloading products:', error);
            }
        }

        isPreloading = false;
        preloadComplete = true;
        if (loadingIndicator) loadingIndicator.classList.add('d-none');
    };

    /**
     * Reveal next batch of preloaded products
     */
    const revealMoreProducts = () => {
        // Get products that haven't been added yet
        const productsToAdd = preloadedProducts.splice(0, 24);

        if (productsToAdd.length === 0) {
            if (preloadComplete && preloadedProducts.length === 0) {
                if (endMessage) endMessage.classList.remove('d-none');
                if (revealObserver) revealObserver.disconnect();
            }
            return;
        }

        productsToAdd.forEach(product => {
            productGrid.appendChild(product);
        });

        // Check if all done
        if (preloadComplete && preloadedProducts.length === 0) {
            if (endMessage) endMessage.classList.remove('d-none');
            if (revealObserver) revealObserver.disconnect();
        }
    };

    /**
     * Initialize scroll-based reveal
     */
    const initRevealOnScroll = () => {
        if (!infiniteScrollTrigger) return;

        if (totalPages <= 1) {
            if (endMessage) endMessage.classList.remove('d-none');
            return;
        }

        if (revealObserver) {
            revealObserver.disconnect();
        }

        revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && preloadedProducts.length > 0) {
                    revealMoreProducts();
                }
            });
        }, {
            root: null,
            rootMargin: '800px',
            threshold: 0
        });

        revealObserver.observe(infiniteScrollTrigger);
    };

    // Fallback reveal loop — IntersectionObserver does not fire reliably in
    // this theme's quirks-mode scroll setup, so poll the trigger position too.
    setInterval(() => {
        if (!infiniteScrollTrigger || preloadedProducts.length === 0) return;
        const rect = infiniteScrollTrigger.getBoundingClientRect();
        if (rect.top < window.innerHeight + 800) {
            revealMoreProducts();
        }
    }, 600);

    /**
     * Reset state and preload products (used when filtering/sorting)
     */
    const resetAndPreload = (doc) => {
        const newTrigger = doc.querySelector('#infinite-scroll-trigger');
        if (newTrigger) {
            currentPage = parseInt(newTrigger.dataset.currentPage);
            totalPages = parseInt(newTrigger.dataset.totalPages);
        }

        preloadedProducts = [];
        isPreloading = false;
        preloadComplete = false;

        if (loadingIndicator) loadingIndicator.classList.add('d-none');

        if (endMessage) {
            if (totalPages <= 1) {
                endMessage.classList.remove('d-none');
            } else {
                endMessage.classList.add('d-none');
            }
        }

        initRevealOnScroll();
        preloadAllProducts();
    };

    /**
     * Fetch ALL product IDs from a collection using Shopify JSON API
     * /collections/{handle}/products.json returns up to 250 per page
     */
    const collectionIdCache = {};
    const fetchCollectionProductIds = async (collectionHandle) => {
        // Return cached result if available
        if (collectionIdCache[collectionHandle]) return collectionIdCache[collectionHandle];

        const ids = new Set();
        let page = 1;

        while (true) {
            const rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
            const response = await fetch(`${rootPath}collections/${collectionHandle}/products.json?limit=250&page=${page}`);
            const data = await response.json();
            const products = data.products;
            if (!products || products.length === 0) break;
            products.forEach(p => ids.add(String(p.id)));
            if (products.length < 250) break;
            page++;
        }

        collectionIdCache[collectionHandle] = ids;
        return ids;
    };

    /**
     * Fetch all pages of a collection's HTML and return matching product cards
     * Fetches page 1 first to get totalPages, then remaining pages in parallel
     */
    const fetchFilteredCards = async (collectionPath, targetIds) => {
        const matchingCards = [];

        // Fetch page 1 to get totalPages
        const separator = collectionPath.includes('?') ? '&' : '?';
        const firstHtml = await fetchHtml(`${collectionPath}${separator}page=1`);
        const firstDoc = new DOMParser().parseFromString(firstHtml, 'text/html');
        const firstGrid = firstDoc.querySelector('#products-grid');
        if (!firstGrid) return matchingCards;

        const trigger = firstDoc.querySelector('#infinite-scroll-trigger');
        const maxPages = trigger ? parseInt(trigger.dataset.totalPages) : 1;

        // Collect matches from page 1
        firstGrid.querySelectorAll(':scope > div').forEach(card => {
            const block = card.querySelector('[data-product-id]');
            if (block && targetIds.has(block.getAttribute('data-product-id'))) {
                matchingCards.push(card.cloneNode(true));
            }
        });

        // Fetch remaining pages in parallel
        if (maxPages > 1) {
            const pagePromises = [];
            for (let p = 2; p <= maxPages; p++) {
                pagePromises.push(fetchHtml(`${collectionPath}${collectionPath.includes('?') ? '&' : '?'}page=${p}`));
            }
            const pages = await Promise.all(pagePromises);
            pages.forEach(html => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const grid = doc.querySelector('#products-grid');
                if (grid) {
                    grid.querySelectorAll(':scope > div').forEach(card => {
                        const block = card.querySelector('[data-product-id]');
                        if (block && targetIds.has(block.getAttribute('data-product-id'))) {
                            matchingCards.push(card.cloneNode(true));
                        }
                    });
                }
            });
        }

        return matchingCards;
    };

    // Track active collection pill for intersection filtering
    let activeCollectionPill = null;

    /**
     * Update the product count in the collection header
     */
    const setProductCount = (count) => {
        const productCount = document.querySelector('.collection-product-count');
        if (!productCount) return;
        const countNumbers = productCount.querySelectorAll('.js-product-count-number');
        if (countNumbers.length) {
            countNumbers.forEach(el => el.textContent = count);
        } else {
            productCount.textContent = count + (count === 1 ? ' product' : ' producten');
        }
    };

    /**
     * Update query parameters and reload filtered results
     */
    const updateParamsAndFilter = (resetPage = true) => {
        const queryParams = getQueryParams();
        const formData = new FormData(filterForm);

        Array.from(filterForm.elements).forEach(element => {
            if (element.name && queryParams[element.name]) {
                delete queryParams[element.name];
            }
        });

        formData.forEach((value, key) => {
            if (value) {
                if (queryParams[key]) {
                    queryParams[key] = [].concat(queryParams[key], value);
                } else {
                    queryParams[key] = value;
                }
            }
        });

        if (resetPage) {
            delete queryParams.page;
        }

        if (Shopify.queryParams.sort_by) {
            queryParams.sort_by = Shopify.queryParams.sort_by;
        }

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            if (Array.isArray(queryParams[key])) {
                queryParams[key].forEach(value => searchParams.append(key, value));
            } else {
                searchParams.append(key, queryParams[key]);
            }
        });

        const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

        window.history.pushState({}, '', urlWithParams);
        Shopify.queryParams = queryParams;

        // If a collection pill is active, apply intersection filtering
        if (activeCollectionPill) {
            const filteredUrl = `${originalCollectionPath}?${searchParams.toString()}`;
            fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
                const matchingCards = await fetchFilteredCards(filteredUrl, targetIds);

                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));

                setProductCount(matchingCards.length);

                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;
                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');
            }).catch(error => console.error('Error loading filtered products:', error));
        } else {
            fetchHtml(urlWithParams)
                .then(html => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');

                    productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;

                    // Update product count in collection header
                    const productCount = document.querySelector('.collection-product-count');
                    const newProductCount = doc.querySelector('.collection-product-count');
                    if (productCount && newProductCount) {
                        productCount.innerHTML = newProductCount.innerHTML;
                    }

                    // Do NOT replace filter form content — filters are applied via "Apply" button
                    // and should remain as-is so users can adjust and re-apply.

                    resetAndPreload(doc);
                })
                .catch(error => console.error('Error loading filtered products:', error));
        }
    };

    /**
     * Handle sort change
     */
    const handleSortChange = (sortValue) => {
        Shopify.queryParams.sort_by = sortValue;

        const queryParams = getQueryParams();
        queryParams.sort_by = sortValue;
        delete queryParams.page;

        Shopify.queryParams = queryParams;

        const searchParams = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
            const val = queryParams[key];
            if (Array.isArray(val)) {
                val.forEach(v => searchParams.append(key, v));
            } else {
                searchParams.append(key, String(val));
            }
        });

        const urlWithParams = `${window.location.pathname}?${searchParams.toString()}`;

        window.history.pushState({}, '', urlWithParams);

        if (activeCollectionPill) {
            fetchCollectionProductIds(activeCollectionPill).then(async (targetIds) => {
                const matchingCards = await fetchFilteredCards(urlWithParams, targetIds);

                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));

                setProductCount(matchingCards.length);

                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;
                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');
            }).catch(error => console.error('Error loading sorted products:', error));
        } else {
            fetchHtml(urlWithParams)
                .then(html => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');

                    productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;

                    // Update product count in collection header
                    const productCount = document.querySelector('.collection-product-count');
                    const newProductCount = doc.querySelector('.collection-product-count');
                    if (productCount && newProductCount) {
                        productCount.innerHTML = newProductCount.innerHTML;
                    }

                    resetAndPreload(doc);
                })
                .catch(error => console.error('Error loading sorted products:', error));
        }
    };

    // Initialize
    initRevealOnScroll();
    preloadAllProducts();

    // Event listeners

    // Quick filter pills and collection pills — single-select across both types
    const quickFilterPills = document.querySelectorAll('.js-quick-filter');
    const collectionPills = document.querySelectorAll('.js-collection-pill');
    const originalCollectionPath = window.location.pathname;

    /**
     * Deactivate all pills (both quick filters and collection pills)
     */
    const deactivateAllPills = () => {
        quickFilterPills.forEach(p => p.classList.remove('is-active'));
        collectionPills.forEach(p => p.classList.remove('is-active'));
        activeCollectionPill = null;
    };

    /**
     * Fetch URL via AJAX and update product grid
     */
    const fetchAndUpdateGrid = (url) => {
        window.history.pushState({}, '', url);
        Shopify.queryParams = getQueryParams();

        fetchHtml(url)
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                productGrid.innerHTML = doc.querySelector('#products-grid').innerHTML;

                const productCount = document.querySelector('.collection-product-count');
                const newProductCount = doc.querySelector('.collection-product-count');
                if (productCount && newProductCount) {
                    productCount.innerHTML = newProductCount.innerHTML;
                }

                resetAndPreload(doc);

                // Scroll to top after filter is applied
                scrollPageToTop();
            })
            .catch(error => console.error('Error applying filter:', error));
    };

    // Quick filter pills — metafield-based filters
    quickFilterPills.forEach(pill => {
        pill.addEventListener('click', function(e) {
            e.preventDefault();
            const param = this.dataset.param;
            const value = this.dataset.value;
            const isActive = this.classList.contains('is-active');

            // Deactivate ALL pills (both types)
            deactivateAllPills();

            if (isActive) {
                // Deselecting — go back to original collection with no filters
                fetchAndUpdateGrid(originalCollectionPath);
            } else {
                // Selecting — clean URL with only this filter on original collection
                this.classList.add('is-active');
                const params = new URLSearchParams();
                params.set(param, value);
                fetchAndUpdateGrid(originalCollectionPath + '?' + params.toString());
            }
        });
    });

    // Collection pills — intersection: show current collection products that also exist in target collection
    collectionPills.forEach(pill => {
        pill.addEventListener('click', async function(e) {
            e.preventDefault();
            const targetHandle = this.dataset.collectionHandle;
            const isActive = this.classList.contains('is-active');

            // Deactivate ALL pills (both types)
            deactivateAllPills();

            if (isActive) {
                // Deselecting — go back to original collection
                activeCollectionPill = null;
                fetchAndUpdateGrid(originalCollectionPath);
            } else {
                this.classList.add('is-active');
                activeCollectionPill = targetHandle;

                // Stop infinite scroll while we work
                if (revealObserver) revealObserver.disconnect();
                preloadedProducts = [];
                preloadComplete = true;
                isPreloading = false;

                // Fetch target IDs using default-language handle (JSON API always uses default handles)
                const targetIds = await fetchCollectionProductIds(targetHandle);
                const matchingCards = await fetchFilteredCards(originalCollectionPath, targetIds);

                // Replace grid with only matching products
                productGrid.innerHTML = '';
                matchingCards.forEach(card => productGrid.appendChild(card));

                // Update count
                setProductCount(matchingCards.length);

                if (endMessage) endMessage.classList.remove('d-none');
                if (loadingIndicator) loadingIndicator.classList.add('d-none');

                // Scroll to top after filter is applied
                scrollPageToTop();
            }
        });
    });

    // Filters are NOT applied on change — only when "Apply" button is clicked
    const applyFilterBtns = document.querySelectorAll('.js-filter-apply');
    applyFilterBtns.forEach(btn => {
        btn.addEventListener('click', function(e) {
            e.preventDefault();
            updateParamsAndFilter(true);
            // Close the filter drawer by clicking a close button (triggers closeFilterPanel + unlockHTML)
            const closeBtn = document.querySelector('.js-drawer-filter-close');
            if (closeBtn) closeBtn.click();
            // Scroll to top after filter is applied
            scrollPageToTop();
        });
    });

    sortSelects.forEach(sortSelect => {
        sortSelect.addEventListener('change', function(e) {
            const newValue = e.target.value;
            sortSelects.forEach(select => {
                if (select !== e.target) {
                    select.value = newValue;
                }
            });
            handleSortChange(newValue);
            // Scroll to top after sorting
            scrollPageToTop();
        });
    });

    // Mobile filter bar sticky positioning — keep below header
    const mobileFilterBar = document.querySelector('.js-mobile-filter-bar');
    if (mobileFilterBar) {
        const updateMobileBarTop = () => {
            const header = document.querySelector('.header');
            const headerMain = header ? header.querySelector('.header-main') : null;
            if (header && header.classList.contains('is-sticky') && headerMain) {
                mobileFilterBar.style.top = headerMain.offsetHeight + 'px';
            } else {
                mobileFilterBar.style.top = '0px';
            }

            // Toggle is-sticky class when bar is stuck
            const currentRect = mobileFilterBar.getBoundingClientRect();
            const stickyTop = parseFloat(mobileFilterBar.style.top) || 0;
            if (currentRect.top <= stickyTop + 1) {
                mobileFilterBar.classList.add('is-sticky');
            } else {
                mobileFilterBar.classList.remove('is-sticky');
            }
        };
        updateMobileBarTop();
        window.addEventListener('scroll', updateMobileBarTop, { passive: true });
        window.addEventListener('resize', updateMobileBarTop, { passive: true });
    }

    // Desktop filter bar sticky positioning — CSS sticky handles the pinning,
    // we only keep the bar below the sticky header by updating its top offset.
    const desktopFilterBar = document.querySelector('.js-collection-sticky-bar');
    if (desktopFilterBar) {
        let deskTicking = false;
        const updateDesktopBarTop = () => {
            const header = document.querySelector('.header');
            const headerMain = header ? header.querySelector('.header-main') : null;
            if (header && header.classList.contains('is-sticky') && headerMain) {
                desktopFilterBar.style.top = headerMain.offsetHeight + 'px';
            } else {
                desktopFilterBar.style.top = '0px';
            }

            // Toggle is-sticky class (used for the shadow) when the bar is stuck
            const rect = desktopFilterBar.getBoundingClientRect();
            const stickyTop = parseFloat(desktopFilterBar.style.top) || 0;
            if (rect.top <= stickyTop + 1) {
                desktopFilterBar.classList.add('is-sticky');
            } else {
                desktopFilterBar.classList.remove('is-sticky');
            }
        };
        const onScrollResize = () => {
            if (!deskTicking) {
                window.requestAnimationFrame(() => {
                    updateDesktopBarTop();
                    deskTicking = false;
                });
                deskTicking = true;
            }
        };
        updateDesktopBarTop();
        window.addEventListener('scroll', onScrollResize, { passive: true });
        window.addEventListener('resize', onScrollResize, { passive: true });
    }
});

// ============================================================================
// Custom sort dropdown - consistent behavior across all screen sizes
// ============================================================================
(function initCustomSortDropdown() {
    const setup = () => {
        const dropdowns = document.querySelectorAll('.js-custom-sort-dropdown');
        if (!dropdowns.length) return;

        dropdowns.forEach(dropdown => {
            const trigger = dropdown.querySelector('.js-sort-trigger');
            const options = dropdown.querySelectorAll('.js-sort-option');
            const hiddenSelect = dropdown.querySelector('.js-sort-by');
            if (!trigger) return;

            // Toggle dropdown on trigger click
            trigger.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                dropdown.classList.toggle('is-open');
            });

            // Handle option selection
            options.forEach(option => {
                option.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();

                    const value = option.dataset.value;
                    const text = option.textContent.trim();

                    // Update visual state across all dropdown instances
                    document.querySelectorAll('.js-sort-option').forEach(opt => {
                        opt.classList.toggle('is-selected', opt.dataset.value === value);
                    });

                    // Update displayed value in all dropdowns (mobile + desktop stay in sync)
                    document.querySelectorAll('.js-sort-value').forEach(el => {
                        el.textContent = text;
                    });

                    // Update hidden select and dispatch native change event
                    // (collection-filtering uses native addEventListener)
                    if (hiddenSelect) {
                        hiddenSelect.value = value;
                        hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }

                    // Close dropdown
                    dropdown.classList.remove('is-open');
                });
            });
        });

        // Close dropdowns when clicking outside
        document.addEventListener('click', function(e) {
            if (!e.target.closest('.js-custom-sort-dropdown')) {
                dropdowns.forEach(d => d.classList.remove('is-open'));
            }
        });

        // Close dropdowns on escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                dropdowns.forEach(d => d.classList.remove('is-open'));
            }
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setup);
    } else {
        setup();
    }
})();

// ============================================================================
// Collection short description - show more/less toggle with dynamic truncation
// ============================================================================
(function initCollectionShortDescriptionToggle() {
    const setup = () => {
        const container = document.querySelector('.collection-short-description');
        if (!container) return;

        const toggle = container.querySelector('.collection-short-description__toggle');
        if (!toggle) return;

        const truncated = container.querySelector('.collection-short-description__truncated');
        const full = container.querySelector('.collection-short-description__full');
        const toggleText = toggle.querySelector('.toggle-text');
        const fullText = full.textContent.trim();

        // Dynamically truncate: 2 lines on mobile, 1 line on desktop/tablet
        const truncateToLines = () => {
            // Reset expanded state on resize
            container.classList.remove('is-expanded');
            full.style.display = 'none';
            truncated.style.display = 'inline';
            toggle.style.display = 'inline';
            toggleText.textContent = toggle.dataset.more;

            const text = container.querySelector('.collection-short-description__text');
            let lineHeight = parseFloat(getComputedStyle(text).lineHeight);
            if (isNaN(lineHeight)) {
                lineHeight = parseFloat(getComputedStyle(text).fontSize) * 1.5;
            }
            const maxLines = window.innerWidth < 768 ? 2 : 1;
            const maxHeight = lineHeight * maxLines;

            truncated.textContent = fullText;

            // Only truncate if text overflows
            if (text.scrollHeight > maxHeight + 1) {
                let low = 0;
                let high = fullText.length;
                while (low < high) {
                    const mid = Math.floor((low + high) / 2);
                    truncated.textContent = fullText.substring(0, mid) + '... ';
                    if (text.scrollHeight > maxHeight + 1) {
                        high = mid;
                    } else {
                        low = mid + 1;
                    }
                }
                const cutoff = Math.max(high - 2, 0);
                truncated.textContent = fullText.substring(0, cutoff).trimEnd() + '... ';

                // Final safety — keep trimming word-by-word if still overflows
                let finalText = fullText.substring(0, cutoff).trimEnd();
                while (text.scrollHeight > maxHeight + 1 && finalText.length > 10) {
                    finalText = finalText
                        .substring(0, finalText.lastIndexOf(' ') || finalText.length - 1)
                        .trimEnd();
                    truncated.textContent = finalText + '... ';
                }
            } else {
                // Text fits — no need for toggle
                toggle.style.display = 'none';
            }
        };

        truncateToLines();

        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(truncateToLines, 150);
        });

        // Toggle expanded state
        toggle.addEventListener('click', function() {
            const isExpanded = container.classList.contains('is-expanded');

            if (isExpanded) {
                // Collapse: show truncated, hide full
                container.classList.remove('is-expanded');
                full.style.display = 'none';
                truncated.style.display = 'inline';
                toggleText.textContent = toggle.dataset.more;
            } else {
                // Expand: show full, hide truncated
                container.classList.add('is-expanded');
                full.style.display = 'inline';
                truncated.style.display = 'none';
                toggleText.textContent = toggle.dataset.less;
            }
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setup);
    } else {
        setup();
    }
})();

// ============================================================================
// Price Range Slider with Filter Integration
// Syncs slider handles with input fields; filters apply via the drawer's
// "Apply" button (form submission reads the synced inputs).
// ============================================================================
(function () {
    class PriceRangeSlider {
        constructor(container) {
            this.container = container;
            this.rangeMin = container.querySelector('.price-range-filter__range-min');
            this.rangeMax = container.querySelector('.price-range-filter__range-max');
            this.inputMin = container.querySelector('.price-range-filter__input-min');
            this.inputMax = container.querySelector('.price-range-filter__input-max');
            this.fill = container.querySelector('.price-range-filter__fill');
            this.valueMinDisplay = container.querySelector('.price-range-filter__value-min-num');
            this.valueMaxDisplay = container.querySelector('.price-range-filter__value-max-num');

            this.max = parseInt(container.getAttribute('data-max')) || 600;
            this.min = 0;
            this.minGap = 10;

            // Prevent circular updates
            this.updatingFromSlider = false;
            this.updatingFromInput = false;

            // Track if user is actively typing (to prevent DOM replacement)
            this.isTyping = false;
            this.typingDebounceTimer = null;
            this.typingDebounceDelay = 800; // milliseconds

            if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax) {
                return;
            }

            this.init();
        }

        init() {
            this.updateFill();
            this.updateDisplayValues();

            // Slider events
            this.rangeMin.addEventListener('input', () => this.handleMinSlider());
            this.rangeMax.addEventListener('input', () => this.handleMaxSlider());

            // Input events
            this.inputMin.addEventListener('input', () => this.handleMinInput());
            this.inputMin.addEventListener('blur', () => this.handleMinInputBlur());
            this.inputMax.addEventListener('input', () => this.handleMaxInput());
            this.inputMax.addEventListener('blur', () => this.handleMaxInputBlur());
        }

        getMinValue() {
            return parseInt(this.rangeMin.value) || this.min;
        }

        getMaxValue() {
            return parseInt(this.rangeMax.value) || this.max;
        }

        updateFill() {
            const minVal = this.getMinValue();
            const maxVal = this.getMaxValue();

            const minPercent = (minVal / this.max) * 100;
            const maxPercent = (maxVal / this.max) * 100;

            this.fill.style.left = `${minPercent}%`;
            this.fill.style.width = `${maxPercent - minPercent}%`;
        }

        updateDisplayValues() {
            const minVal = this.getMinValue();
            const maxVal = this.getMaxValue();

            if (this.valueMinDisplay) {
                this.valueMinDisplay.textContent = minVal;
            }
            if (this.valueMaxDisplay) {
                this.valueMaxDisplay.textContent = maxVal;
            }
        }

        validateValue(value, isMin) {
            if (value === '' || value === null || value === undefined) {
                return isMin ? this.min : this.max;
            }

            let num = parseInt(value);
            if (isNaN(num)) {
                return isMin ? this.min : this.max;
            }

            if (num < this.min) num = this.min;
            if (num > this.max) num = this.max;

            // Enforce min gap
            if (isMin) {
                const maxVal = this.getMaxValue();
                if (maxVal - num < this.minGap) {
                    num = maxVal - this.minGap;
                    if (num < this.min) num = this.min;
                }
            } else {
                const minVal = this.getMinValue();
                if (num - minVal < this.minGap) {
                    num = minVal + this.minGap;
                    if (num > this.max) num = this.max;
                }
            }

            return num;
        }

        handleMinSlider() {
            if (this.updatingFromInput) return;

            this.updatingFromSlider = true;

            let value = this.getMinValue();
            const maxVal = this.getMaxValue();

            if (maxVal - value < this.minGap) {
                value = maxVal - this.minGap;
                this.rangeMin.value = value;
            }

            this.inputMin.value = value;
            this.updateFill();
            this.updateDisplayValues();

            this.updatingFromSlider = false;
        }

        handleMaxSlider() {
            if (this.updatingFromInput) return;

            this.updatingFromSlider = true;

            let value = this.getMaxValue();
            const minVal = this.getMinValue();

            if (value - minVal < this.minGap) {
                value = minVal + this.minGap;
                this.rangeMax.value = value;
            }

            this.inputMax.value = value;
            this.updateFill();
            this.updateDisplayValues();

            this.updatingFromSlider = false;
        }

        setTypingState(isTyping) {
            this.isTyping = isTyping;
            this.container.dataset.isTyping = isTyping ? 'true' : 'false';
        }

        handleMinInput() {
            if (this.updatingFromSlider) return;

            this.updatingFromInput = true;

            this.setTypingState(true);
            if (this.typingDebounceTimer) {
                clearTimeout(this.typingDebounceTimer);
            }
            this.typingDebounceTimer = setTimeout(() => {
                this.setTypingState(false);
            }, this.typingDebounceDelay);

            // Don't validate or constrain while typing - let user type freely
            const inputValue = this.inputMin.value;

            if (inputValue !== '' && !isNaN(parseInt(inputValue))) {
                const numValue = parseInt(inputValue);

                if (numValue >= 0) {
                    let sliderValue = numValue > this.max ? this.max : numValue;

                    const maxVal = this.getMaxValue();
                    if (maxVal - sliderValue < this.minGap) {
                        sliderValue = maxVal - this.minGap;
                        if (sliderValue < this.min) sliderValue = this.min;
                    }

                    this.rangeMin.value = sliderValue;
                    this.updateFill();
                }

                if (this.valueMinDisplay) {
                    this.valueMinDisplay.textContent = numValue > this.max ? this.max : numValue;
                }
            }

            this.updatingFromInput = false;
        }

        handleMinInputBlur() {
            if (this.updatingFromSlider) return;

            this.updatingFromInput = true;

            if (!this.inputMin.value || this.inputMin.value === '') {
                this.inputMin.value = this.min;
                this.rangeMin.value = this.min;
            } else {
                const value = this.validateValue(this.inputMin.value, true);
                this.inputMin.value = value;
                this.rangeMin.value = value;
            }

            this.updateFill();
            this.updateDisplayValues();

            this.updatingFromInput = false;
        }

        handleMaxInput() {
            if (this.updatingFromSlider) return;

            this.updatingFromInput = true;

            this.setTypingState(true);
            if (this.typingDebounceTimer) {
                clearTimeout(this.typingDebounceTimer);
            }
            this.typingDebounceTimer = setTimeout(() => {
                this.setTypingState(false);
            }, this.typingDebounceDelay);

            // Don't validate or constrain while typing - let user type freely
            const inputValue = this.inputMax.value;

            if (inputValue !== '' && !isNaN(parseInt(inputValue))) {
                const numValue = parseInt(inputValue);

                if (numValue >= 0) {
                    let sliderValue = numValue > this.max ? this.max : numValue;

                    const minVal = this.getMinValue();
                    if (sliderValue - minVal < this.minGap) {
                        sliderValue = minVal + this.minGap;
                        if (sliderValue > this.max) sliderValue = this.max;
                    }

                    this.rangeMax.value = sliderValue;
                    this.updateFill();
                }

                if (this.valueMaxDisplay) {
                    this.valueMaxDisplay.textContent = numValue > this.max ? this.max : numValue;
                }
            }

            this.updatingFromInput = false;
        }

        handleMaxInputBlur() {
            if (this.updatingFromSlider) return;

            this.updatingFromInput = true;

            if (!this.inputMax.value || this.inputMax.value === '') {
                this.inputMax.value = this.max;
                this.rangeMax.value = this.max;
            } else {
                const value = this.validateValue(this.inputMax.value, false);
                this.inputMax.value = value;
                this.rangeMax.value = value;
            }

            this.updateFill();
            this.updateDisplayValues();

            this.updatingFromInput = false;
        }
    }

    // Initialize all price sliders on page load and after AJAX updates
    function initPriceSliders() {
        const containers = document.querySelectorAll('.price-range-filter');
        containers.forEach(container => {
            if (container.priceSliderInstance) {
                container.priceSliderInstance = null;
            }
            container.priceSliderInstance = new PriceRangeSlider(container);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPriceSliders);
    } else {
        initPriceSliders();
    }

    // Re-initialize after AJAX updates (for dynamic content)
    document.addEventListener('filterUpdated', initPriceSliders);

    // Export for use in other scripts
    window.initPriceSliders = initPriceSliders;
})();
